import React from 'react';

import { SpendTypeEnum } from 'components/Classification';
import { ApiContext } from 'context/ApiContext';
import { ApplicationAllocation } from './ApplicationAllocation';
import { Subheader } from 'components/SpendAllocation/Subheader';
import { ErrorPage } from 'views/Error';
import { ReuseAllocationModal } from 'components/SpendAllocation/ReuseAllocationModal';
import { ChangeCategoryModal } from 'components/SpendAllocation/ChangeCategoryModal';
import { NavigateUnsavedModal } from 'components/SpendAllocation/NavigateUnsavedModal';
import { DateSwitchModal } from 'components/SpendAllocation/DateSwitchModal';
import { LoaderWrapper, Wrapper, NavigationPromptWrapper } from '../styles';
import { ISettingsResponse, ICurrencyFormat } from 'utils/types';
import NavigationPrompt from 'react-router-navigation-prompt';
import { getQueryDatesFromPeriod } from 'context/QueryParams';
import {
  getApplications,
  getDimensions,
  getAllocations,
  updateAllocations,
} from 'utils/api';
import {
  ApLoaderDefault,
  ApLoaderText,
  IOption,
} from '@alixpartners/ui-components';
import {
  dimensionOptions,
  isAllocationItemValid,
  contributionField,
  contributionQueryParam,
} from 'components/SpendAllocation/utils';
import {
  IDimensionGroup,
  IAllocatedYear,
  IApplicationItem,
  IAllocatedItem,
} from 'components/SpendAllocation/types';
import {
  createPeriodsFromRange,
  getPeriodStr,
} from 'views/Settings/settings.utils';
import isEqual from 'lodash/isEqual';

interface IState {
  selectedDate: IOption;
  dateToChange: IOption | null;
  dimensions: IDimensionGroup[];
  applications: IApplicationItem[];
  allocationsByYears: IAllocatedYear[];
  allocations: IAllocatedItem[];
  error: any;
  isLoading: boolean;
  isSaveActive: boolean;
  dimension: IOption;
  dimensionToChange: IOption | null;
  isReuseModalOpen: boolean;
  isSaving: boolean;
  isDimensionsLoading: boolean;
  isChangeCategoryModalOpen: boolean;
  isDateSwitchModalOpen: boolean;
}

interface IDefaultProps {
  spendType: SpendTypeEnum;
}

interface IProps extends IDefaultProps {
  settings: ISettingsResponse;
  permissions: string[];
  format: ICurrencyFormat;
}

const BACK_URL = '/settings/data/applications';

export class ApplicationAllocationContainer extends React.Component<
  IProps,
  IState
> {
  state = {
    selectedDate: this.getInitialSelectedDate(),
    error: null as IState['error'],
    allocationsByYears: [] as IState['allocationsByYears'],
    allocations: [] as IState['allocations'],
    dimensions: [],
    applications: [],
    isLoading: false,
    isSaveActive: false,
    isDimensionsLoading: false,
    isSaving: false,
    dimensionToChange: null as IState['dimensionToChange'],
    dateToChange: null as IState['dateToChange'],
    isReuseModalOpen: false,
    isChangeCategoryModalOpen: false,
    isDateSwitchModalOpen: false,
    dimension: dimensionOptions[this.props.spendType][0],
  };

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { dimension, allocationsByYears, selectedDate, allocations } =
      this.state;

    if (selectedDate.value !== prevState.selectedDate.value) {
      this.setState(
        {
          allocations: this.getAllocationsBySelectedDate(),
        },
        () => this.setDimensionFromAllocations(this.fetchDimensions)
      );
    }

    if (dimension.value !== prevState.dimension.value) {
      this.setAllocationsByDimension();
    }

    if (!isEqual(allocations, prevState.allocations)) {
      this.validateSaveButton();
    }

    if (allocationsByYears !== prevState.allocationsByYears) {
      this.setState({
        allocations: this.getAllocationsBySelectedDate(),
      });
    }
  }

  fetchData = () => {
    this.setState({ error: null, isLoading: true });

    return Promise.all([this.fetchAllocations(), this.fetchApplications()])
      .then(this.fetchDimensions)
      .then(() => {
        this.setState({
          isLoading: false,
        });
      })
      .catch((error) => {
        this.setState({ error, isLoading: false });
      });
  };

  getSelectedDateParams = () => {
    const { settings } = this.props;
    const { selectedDate } = this.state;
    const currentPeriod = {
      ...settings.selectedYear,
      startYear: Number(selectedDate.value),
      endYear: Number(selectedDate.value) + 1,
    };

    return getQueryDatesFromPeriod(currentPeriod);
  };

  setAllocationsByDimension = () => {
    const { dimension } = this.state;

    const activeAllocations = this.getAllocationsBySelectedDate();

    if (
      activeAllocations.length > 0 &&
      activeAllocations[0].type === dimension.value
    ) {
      this.setState({
        allocations: activeAllocations,
      });
    }
  };

  getAllocationsBySelectedDate = () => {
    const { allocationsByYears, selectedDate } = this.state;

    return (
      allocationsByYears.find(
        (item: IAllocatedYear) => String(item.year) === selectedDate.value
      )?.allocations || []
    );
  };

  fetchAllocations = () => {
    const { spendType } = this.props;

    return getAllocations({
      contribution: contributionField[spendType],
    })
      .then((allocationsByYears) => {
        this.setState({
          allocationsByYears: allocationsByYears || [],
        });
      })
      .then(() => this.setDimensionFromAllocations());
  };

  fetchDimensions = () => {
    const { dimension } = this.state;
    const { spendType } = this.props;

    this.setState({ isDimensionsLoading: true });
    const params = {
      ...this.getSelectedDateParams(),
      contributions: contributionQueryParam[spendType],
    };

    return getDimensions(params, dimension.value).then((dimensionResp) => {
      this.setState({
        dimensions: (dimensionResp && dimensionResp.spendItems) || [],
        isDimensionsLoading: false,
      });
    });
  };

  fetchApplications = () => {
    return getApplications().then((applications) => {
      this.setState({
        applications,
      });
    });
  };

  getInitialSelectedDate() {
    const { settings } = this.props;
    const { selectedYear } = settings;
    const options = this.getDateOptions();

    const selectedOption = options.find(
      (item) => item.value === String(selectedYear.startYear)
    );

    return selectedOption
      ? selectedOption
      : options[0] || { label: 'No data', value: 0 };
  }

  getDateOptions() {
    const { settings } = this.props;
    const { availableRange, selectedYear } = settings;

    const availableYears = createPeriodsFromRange(
      availableRange,
      selectedYear.startPeriod
    );

    return availableYears.reverse().map((item) => {
      return { label: getPeriodStr(item), value: String(item.startYear) };
    });
  }

  saveAllocation = () => {
    const { allocations, selectedDate } = this.state;
    const { spendType } = this.props;

    this.setState({ isSaving: true });

    return updateAllocations(
      [{ year: Number(selectedDate.value), allocations }],
      contributionField[spendType]
    ).then(() => {
      this.setState({ isSaving: false, isSaveActive: false });
      this.fetchAllocations();
    });
  };

  validateSaveButton = () => {
    this.setState({
      isSaveActive: this.hasAllocationsChanged() && this.isAllocationsValid(),
    });
  };

  isAllocationsValid = () => {
    const { allocations } = this.state;

    return !allocations.some((item) => !isAllocationItemValid(item.items));
  };

  hasAllocationsChanged = () => {
    const { allocations } = this.state;
    const originalAllocations = this.getAllocationsBySelectedDate();

    return !isEqual(originalAllocations, allocations);
  };

  getAllocatedYears = () => {
    const { allocationsByYears } = this.state;

    return allocationsByYears.map((item) => String(item.year));
  };

  handleDimensionChange = (option: IOption | IOption[]) => {
    const { allocations, dimension } = this.state;

    if (Array.isArray(option) || option.value === dimension.value) {
      return;
    }

    if (!allocations || allocations.length === 0) {
      this.setState({ dimension: option }, this.fetchDimensions);

      return;
    }

    this.openChangeCategoryModal();
    this.setState({ dimensionToChange: option });
  };

  handleDateChange = (option: IOption) => {
    if (!this.hasAllocationsChanged()) {
      this.setState({ selectedDate: option });

      return;
    }

    this.openDateSwitchModal();
    this.setState({ dateToChange: option });
  };

  changeDateFromModal = () => {
    const { dateToChange } = this.state;

    if (!dateToChange) {
      return;
    }

    this.setState({
      selectedDate: dateToChange,
    });
    this.closeDateSwitchModal();
  };

  saveAndChangeDateFromModal = () => {
    this.saveAllocation();
    this.changeDateFromModal();
  };

  openChangeCategoryModal = () => {
    this.setState({
      isChangeCategoryModalOpen: true,
    });
  };

  closeChangeCategoryModal = () => {
    this.setState({
      isChangeCategoryModalOpen: false,
      dimensionToChange: null,
    });
  };

  openDateSwitchModal = () => {
    this.setState({
      isDateSwitchModalOpen: true,
    });
  };

  closeDateSwitchModal = () => {
    this.setState({
      isDateSwitchModalOpen: false,
      dateToChange: null,
    });
  };

  changeDimensionFromModal = () => {
    const { dimensionToChange } = this.state;

    if (!dimensionToChange) {
      return;
    }

    this.setState(
      {
        dimension: dimensionToChange,
        allocations: [],
      },
      this.fetchDimensions
    );
    this.closeChangeCategoryModal();
  };

  setDimensionFromAllocations = (callback?: () => void) => {
    const { allocations, dimension } = this.state;
    const { spendType } = this.props;

    if (allocations.length > 0 && allocations[0].type !== dimension.value) {
      this.setState(
        {
          dimension:
            dimensionOptions[spendType].find(
              (item) => item.value === allocations[0].type
            ) || dimensionOptions[spendType][0],
        },
        callback
      );

      return;
    }

    if (callback) {
      callback();
    }
  };

  reuseAllocation = (year: number) => {
    const { allocationsByYears, selectedDate, dimension } = this.state;

    const reusedAllocations =
      allocationsByYears.find((item: IAllocatedYear) => item.year === year)
        ?.allocations || [];

    this.setState(
      {
        allocations: reusedAllocations.map((allocation) => ({
          ...allocation,
          year: Number(selectedDate.value),
        })),
      },
      () =>
        this.setDimensionFromAllocations(() => {
          if (reusedAllocations[0].type !== dimension.value) {
            this.fetchDimensions();
          }
        })
    );
    this.closeReuseAllocationModal();
  };

  getReuseAllocationOptions = () => {
    const { selectedDate } = this.state;
    const dateOptions = this.getDateOptions();
    const allocatedYears = this.getAllocatedYears();
    const inheritAllocations = allocatedYears
      ? allocatedYears.filter(
          (item) =>
            item !== selectedDate.value &&
            dateOptions.find((dateOption) => dateOption.value === item)
        )
      : [];

    if (!inheritAllocations || inheritAllocations.length === 0) {
      return null;
    }

    const options = dateOptions.filter(
      (item) => item.value !== selectedDate.value
    );

    return options.map((item) => ({
      ...item,
      isAllocated: allocatedYears.includes(item.value),
    }));
  };

  changeAllocation = (allocation: IAllocatedItem) => {
    const { allocations } = this.state;
    const changedAllocationIndex = allocations.findIndex(
      (item) => item.dimensionId === allocation.dimensionId
    );
    let updatedAllocations = [];

    if (allocation.items.length > 0) {
      if (changedAllocationIndex !== -1) {
        updatedAllocations = [
          ...allocations.filter((item, index) => {
            return index !== changedAllocationIndex;
          }),
          allocation,
        ];
      } else {
        updatedAllocations = [...allocations, allocation];
      }
    } else {
      updatedAllocations = allocations.filter((item, index) => {
        return index !== changedAllocationIndex;
      });
    }

    this.setState({
      allocations: updatedAllocations,
    });
  };

  openReuseAllocationModal = () => {
    this.setState({ isReuseModalOpen: true });
  };

  closeReuseAllocationModal = () => {
    this.setState({ isReuseModalOpen: false });
  };

  render() {
    const { spendType, permissions, format } = this.props;
    const {
      selectedDate,
      error,
      dimensions,
      isLoading,
      isSaveActive,
      applications,
      allocations,
      isReuseModalOpen,
      isChangeCategoryModalOpen,
      isDateSwitchModalOpen,
      dimension,
      isSaving,
      isDimensionsLoading,
    } = this.state;

    return (
      <React.Fragment>
        <NavigationPromptWrapper>
          <NavigationPrompt
            disableNative
            when={(currentLocation, nextLocation) =>
              currentLocation.pathname !== nextLocation?.pathname &&
              this.hasAllocationsChanged()
            }
          >
            {({ onConfirm, onCancel }) => (
              <NavigateUnsavedModal
                onSave={() => {
                  this.saveAllocation().then(onConfirm);
                }}
                onClose={onCancel}
                onConfirm={onConfirm}
                isInvalid={!this.isAllocationsValid()}
              />
            )}
          </NavigationPrompt>
        </NavigationPromptWrapper>
        <Subheader
          dateOptions={this.getDateOptions()}
          selectedDate={selectedDate}
          onSave={this.saveAllocation}
          onDateChange={this.handleDateChange}
          spendType={spendType}
          isSaveActive={isSaveActive && !isSaving}
          backUrl={BACK_URL}
          title="application"
        />
        {error ? (
          <ErrorPage
            title="Spend allocation"
            description={error.message || ''}
            onClick={this.fetchData}
          />
        ) : isLoading ? (
          <LoaderWrapper>
            <ApLoaderDefault loaderText={() => <ApLoaderText />} />
          </LoaderWrapper>
        ) : (
          <ApplicationAllocation
            spendType={spendType}
            permissions={permissions}
            format={format}
            dimensions={dimensions}
            allocations={allocations}
            applications={applications}
            onReuseAllocation={this.openReuseAllocationModal}
            onDimensionChange={this.handleDimensionChange}
            onAllocationChange={this.changeAllocation}
            dimension={dimension}
            selectedYear={Number(selectedDate.value)}
            isDimensionsLoading={isDimensionsLoading}
          />
        )}
        {isReuseModalOpen && (
          <ReuseAllocationModal
            onSubmit={this.reuseAllocation}
            onClose={this.closeReuseAllocationModal}
            isOpen={isReuseModalOpen}
            options={this.getReuseAllocationOptions()}
          />
        )}
        {isChangeCategoryModalOpen && (
          <ChangeCategoryModal
            onSubmit={this.changeDimensionFromModal}
            onClose={this.closeChangeCategoryModal}
            isOpen={isChangeCategoryModalOpen}
          />
        )}
        {isDateSwitchModalOpen && (
          <DateSwitchModal
            onSave={this.saveAndChangeDateFromModal}
            onClose={this.closeDateSwitchModal}
            onSwitch={this.changeDateFromModal}
            isOpen={isDateSwitchModalOpen}
            isInvalid={!this.isAllocationsValid()}
          />
        )}
      </React.Fragment>
    );
  }
}

export default (props: IDefaultProps) => (
  <Wrapper>
    <ApiContext.Consumer>
      {({ permissions, settings, format }) => {
        if (!settings) {
          return (
            <LoaderWrapper>
              <ApLoaderDefault loaderText={() => <ApLoaderText />} />
            </LoaderWrapper>
          );
        }

        return (
          <ApplicationAllocationContainer
            {...props}
            settings={settings}
            permissions={permissions}
            format={format}
          />
        );
      }}
    </ApiContext.Consumer>
  </Wrapper>
);
